import { css, cx } from "@emotion/css";
import { BoldIcon, CodeIcon, ImageIcon, ItalicIcon, LinkIcon, ListIcon, ListOlIcon, QuotesIcon } from "@octopusdeploy/design-system-icons";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useCallback, useState, useRef } from "react";
import type { ReactNode } from "react";
import { Button } from "../../Button";
import { ExternalLink } from "../../Link";
import { Note } from "../Note";
import { Text } from "../Text";
import type { TextInput } from "../Text";
import { MarkdownProvider } from "./MarkdownProvider";
type MarkdownTextInputRef = {
    focus: () => void;
    insertAtCursor: (val: string) => void;
};
interface MarkdownEditorProps {
    value: string | undefined;
    onChange: (newValue: string) => void;
    accessibleName?: string;
    autoFocus?: boolean;
    placeholder?: string;
    bottomNoteText?: string | null;
    restrictHeight?: boolean;
    disabled?: boolean;
    label?: string;
    error?: string;
    hideMarkdownToggle?: boolean;
    controlsInitiallyVisible?: boolean;
    onValidate?(value: string): boolean;
    textInputRef?(input: MarkdownTextInputRef | null): void;
}
export function MarkdownEditor({ onChange, onValidate, hideMarkdownToggle, restrictHeight, label, error, accessibleName, value, disabled, placeholder, textInputRef, controlsInitiallyVisible = false, autoFocus = false, bottomNoteText = null, }: MarkdownEditorProps) {
    const { inputRef, actions: markdownActions } = useTextInputMarkdownActions(value);
    const handleChange = (value: string) => {
        const valid = onValidate?.(value) ?? true;
        if (valid) {
            onChange?.(value);
        }
    };
    //We currently treat the visibility for the markdown controls as a non-controlled prop. Ideally this
    //would be controlled, however, that would also mean that every consumer has to manage this state.
    const [showMarkdownControls, setShowMarkdownControls] = useState(controlsInitiallyVisible);
    const markdownTitle = `${showMarkdownControls ? "Hide" : "Show"} markdown controls`;
    const showMarkdownControlsToggle = hideMarkdownToggle ? null : (<Button label={markdownTitle} importance={"quiet"} onClick={(e) => {
            e.preventDefault();
            setShowMarkdownControls((prev) => !prev);
        }}/>);
    return (<div className={cx(styles.markdownEditorContainer, restrictHeight && styles.restrictedHeight)}>
            <div className={cx({ [styles.markdownEditor]: showMarkdownControls })}>
                {showMarkdownControls && (<div className={styles.toolbar}>
                        {markdownActions.map(({ type, action }, index) => (<MarkdownEditorButton key={`${index}_${type}`} type={type} onClick={action}/>))}
                    </div>)}

                <Text textInputRef={(text: TextInput) => {
            if (textInputRef) {
                textInputRef(text);
            }
            inputRef.current = text;
        }} value={value ? value : ""} onChange={handleChange} error={error} label={label} placeholder={placeholder} multiline={true} accessibleName={accessibleName} minRows={showMarkdownControls ? 3 : 2} // 2 or 3 is enough to hint that this is a multi-line editor.
     showBorder={false} className={styles.inputContainer} hideUnderline={true} autoFocus={autoFocus} disabled={disabled}/>
            </div>
            {bottomNoteText && <Note>{bottomNoteText}</Note>}
            {showMarkdownControls ? (<div className={styles.markdownControlsContainer}>
                    {showMarkdownControlsToggle}
                    <ExternalLink href="MasteringMarkdown">Markdown instructions</ExternalLink>
                </div>) : (showMarkdownControlsToggle)}
        </div>);
}
function useTextInputMarkdownActions(value: string | undefined) {
    const inputRef = useRef<TextInput | null>(null);
    const getSelection = useCallback(() => {
        const selection = inputRef.current?.getSelection();
        return selection ?? { start: null, end: null };
    }, []);
    const setInputState = (value: string, selectionStart: number, selectionEnd: number) => {
        const selection = {
            start: selectionStart,
            end: selectionEnd,
        };
        inputRef.current?.setValueAndSelection(selection, value);
        inputRef.current?.focus();
    };
    const getInputState = (): MarkdownProvider => {
        const selection = getSelection();
        return new MarkdownProvider(value ? value : "", selection.start ?? 0, selection.end ?? 0);
    };
    const bold = () => {
        getInputState().insertBefore("**").insertAfter("**").emptySelectionText("Bold text").apply(setInputState);
    };
    const italic = () => {
        getInputState().insertBefore("_").insertAfter("_").emptySelectionText("Italic text").apply(setInputState);
    };
    const bullet = () => {
        getInputState().surroundWithNewlines().insertBefore("- ").emptySelectionText("List item").apply(setInputState);
    };
    const number = () => {
        getInputState().surroundWithNewlines().insertBefore("1. ").emptySelectionText("List item").apply(setInputState);
    };
    const quote = () => {
        getInputState().surroundWithNewlines().insertBefore("> ").insertAfterNewlineInSelection("> ").emptySelectionText("Quoted text").apply(setInputState);
    };
    const code = () => {
        getInputState().surroundWithNewlines().insertBefore("```\n").insertAfter("\n```").emptySelectionText("Code").apply(setInputState);
    };
    const link = () => {
        const url = prompt("Please enter a url for the link:", "http://");
        if (url) {
            getInputState().insertBefore("[").insertAfter(`](${url})`).emptySelectionText("Enter link description here").apply(setInputState);
        }
    };
    const image = () => {
        const url = prompt("Please enter a url for the image:", "http://");
        if (url) {
            getInputState().insertBefore("![").insertAfter(`](${url})`).emptySelectionText("Enter image description here").apply(setInputState);
        }
    };
    return {
        inputRef,
        actions: [
            { type: "Bold", action: bold },
            { type: "Bulleted List", action: bullet },
            { type: "Code", action: code },
            { type: "Link", action: link },
            { type: "Numeric list", action: number },
            { type: "Italic", action: italic },
            { type: "Image", action: image },
            { type: "Quotes", action: quote },
        ] as const satisfies Array<{
            type: EditorActionTypes;
            action: () => void;
        }>,
    };
}
const styles = {
    markdownEditorContainer: css({
        position: "relative",
        width: "100%",
    }),
    markdownEditor: css({
        outline: `1px solid ${themeTokens.color.textField.border.default}`,
        marginBottom: space[8],
        "&:hover": {
            outline: `2px solid ${themeTokens.color.textField.border.hover}`,
        },
        "&:has(div.MuiInputBase-root.MuiInput-root.Mui-focused)": {
            outline: `2px solid ${themeTokens.color.textField.border.active}`,
        },
        "& > div:has(div.MuiInputBase-root.MuiInput-root)": {
            padding: "10px 10px 0 10px",
        },
        "& .MuiInput-underline::before, & .MuiInput-underline:hover:not(.Mui-disabled)::before": {
            borderBottom: "none",
        },
        "& .MuiInput-underline::after": {
            borderBottom: "none",
        },
        "& label.MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-formControl": {
            transform: "translate(0, 8px) scale(1)",
            "&.Mui-focused": {
                display: "none",
            },
            "&.MuiFormLabel-filled": {
                display: "none",
            },
        },
        "& .MuiInputBase-root.MuiInput-root.MuiInputBase-formControl.MuiInput-formControl.MuiInputBase-multiline.MuiInput-multiline": {
            margin: 0,
            padding: 0,
        },
    }),
    markdownControlsContainer: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "baseline",
        button: {
            marginRight: space[8],
        },
    }),
    restrictedHeight: css({
        maxHeight: "15rem",
    }),
    toolbar: css({
        color: themeTokens.color.icon.secondary,
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-end",
        backgroundColor: themeTokens.color.background.secondary.default,
        padding: `0 ${space[8]}`,
        gap: space[12],
    }),
    inputContainer: css({
        width: "100%",
    }),
};
interface MarkdownEditorButtonProps {
    type: EditorActionTypes;
    onClick: () => void;
}
type EditorActionTypes = "Bold" | "Italic" | "Bulleted List" | "Numeric list" | "Image" | "Link" | "Code" | "Quotes";
const markdownEditorIconMap: Record<EditorActionTypes, ReactNode> = {
    "Bulleted List": <ListIcon size={20}/>,
    Bold: <BoldIcon size={20}/>,
    Code: <CodeIcon size={20}/>,
    Italic: <ItalicIcon size={20}/>,
    Link: <LinkIcon size={20}/>,
    "Numeric list": <ListOlIcon size={20}/>,
    Image: <ImageIcon size={20}/>,
    Quotes: <QuotesIcon size={20}/>,
};
function MarkdownEditorButton({ onClick, type }: MarkdownEditorButtonProps) {
    return <Button importance={"ghost"} onClick={onClick} accessibleName={type} icon={markdownEditorIconMap[type]} aria-hidden="true" title={type}/>;
}
